import BaseService from './base.service';

class QualityObservationService extends BaseService {
  /**
   * Create a new quality observation
   * @param {FormData} data - Quality observation data with video as FormData
   * @returns {Promise<Object>} Created quality observation
   */
  create(data) {
    return this.sendFormData('POST', 'quality-observation', data);
  }

  /**
   * Get simplified quality observation details by ID
   * @param {number} id - Quality observation ID
   * @returns {Promise<Object>} Simplified quality observation details
   */
  getDetailsById(id) {
    return this.get(`quality-observation/${id}/details`);
  }

  /**
   * Update a quality observation
   * @param {number} id - Quality observation ID
   * @param {Object} data - Updated quality observation data
   * @returns {Promise<Object>} Updated quality observation
   */
  update(id, data) {
    return this.put(`quality-observation/${id}`, data);
  }

  /**
   * Delete a quality observation
   * @param {number} id - Quality observation ID
   * @returns {Promise<Object>} Success response
   */
  remove(id) {
    return this.delete(`quality-observation/${id}`);
  }

  /**
   * Get quality observation details
   * @param {number} id - Quality observation ID
   * @returns {Promise<Object>} Quality observation details
   */
  getQualityObservationDetails(id) {
    return this.get(`quality-observation/${id}`);
  }

  /**
   * Get materials attached to a quality observation
   * @param {number} id - Quality observation ID
   * @returns {Promise} - Promise with materials data
   */
  getObservationMaterials(id) {
    return this.get(`quality-observation/${id}/materials`);
  }

  /**
   * Update the order of materials attached to a quality observation
   * @param {number} id - Quality observation ID
   * @param {Array<number>} materialIds - Array of material IDs in the new order
   * @returns {Promise} - Promise with updated materials data
   */
  updateMaterialOrder(id, materialIds) {
    return this.put(`quality-observation/${id}/materials/order`, { materialIds });
  }

  /**
   * Attach materials to a quality observation
   * @param {number} id - Quality observation ID
   * @param {Array<number>} materialIds - Array of material IDs to attach
   * @returns {Promise} - Promise with updated observation data
   */
  attachMaterials(id, materialIds) {
    return this.post(`quality-observation/${id}/materials`, { materialIds });
  }

  /**
   * Detach a material from a quality observation
   * @param {number} id - Quality observation ID
   * @param {number} materialId - Material ID to detach
   * @returns {Promise} - Promise with success message
   */
  detachMaterial(id, materialId) {
    return this.delete(`quality-observation/${id}/materials/${materialId}`);
  }

  /**
   * Update the order of resources attached to a quality observation
   * @param {number} id - Quality observation ID
   * @param {Array<number>} resourceIds - Array of resource IDs in the new order
   * @returns {Promise} - Promise with updated resources data
   */
  updateResourceOrder(id, resourceIds) {
    return this.put(`quality-observation/${id}/resource-order`, { resourceIds });
  }

  /**
   * Archive a quality observation
   * @param {number} id - Quality observation ID
   * @returns {Promise<Object>} Archived quality observation
   */
  archive(id) {
    return this.put(`quality-observation/${id}/archive`);
  }

  /**
   * Activate a quality observation
   * @param {number} id - Quality observation ID
   * @returns {Promise<Object>} Activated quality observation
   */
  activate(id) {
    return this.put(`quality-observation/${id}/activate`);
  }

  /**
   * Get all quality observations with filters (from worklist endpoint)
   * @param {Object} queryParams - Query parameters for filtering
   * @returns {Promise<Object>} List of quality observations
   */
  getAll(queryParams = {}) {
    const queryString = new URLSearchParams(queryParams).toString();
    return this.get(`worklist${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Set a quality observation status to on hold with a reason
   * @param {number} id - Quality observation ID
   * @param {Object} data - Object containing onHoldReason
   * @returns {Promise<Object>} Updated quality observation
   */
  setOnHold(id, data) {
    return this.put(`quality-observation/${id}/on-hold`, data);
  }

  /**
   * Set a quality observation status to complete
   * @param {number} id - Quality observation ID
   * @returns {Promise<Object>} Updated quality observation
   */
  complete(id) {
    return this.put(`quality-observation/${id}/complete`);
  }
}

export default new QualityObservationService();
